import { Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useCaseMutation from "@/hooks/useCaseMutation";
import { cn } from "@/libraries/cn";
import { QUERY_KEYS } from "@/libraries/constant";
import { formatDate, fromMsToTime } from "@/libraries/date";
import {
	getAskHemiShareQueryOptions,
	getCareChatQueryOptions,
	getWoundAnalysisQueryOptions,
	listWoundAnalysesQueryOptions,
} from "@/queries";
import { CASE_TABS_VALUE } from "@/schemas";
import { extractPlan, useCaseClinicalStore } from "@/stores/caseClinical";
import type { ResError } from "@/types";
import type { WoundAnalysis, WoundTrajectoryDirection } from "@/types/askhemi";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

const WoundAnalysisSearchSchema = z.object({
	shareId: z.coerce.number().int().optional(),
});

export const Route = createFileRoute("/dashboard/case/view/$id/wound-analysis")(
	{
		validateSearch: zodSearchValidator(WoundAnalysisSearchSchema),
		component: RouteComponent,
	},
);

const STEP_KEYS = ["step1", "step2", "step3", "step4"] as const;

const METRIC_LABEL_TRANSLATIONS: Record<string, string> = {
	"estimated area": "预估面积",
	"wound area": "伤口面积",
	"tissue granulation": "肉芽组织",
	"granulation tissue": "肉芽组织",
	granulation: "肉芽组织",
	exudate: "渗出液",
	"exudate level": "渗出液水平",
	epithelialization: "上皮化",
	necrosis: "坏死组织",
	"necrotic tissue": "坏死组织",
	slough: "腐肉",
	erythema: "红斑",
	edema: "水肿",
	size: "尺寸大小",
	area: "面积",
	depth: "深度",
	maceration: "浸渍",
};

const formatDayLabel = (label: string, isZh: boolean) => {
	if (!isZh) return label;
	const match = label.match(/^Day\s*(\d+)/i);
	if (match) return `第 ${match[1]} 天`;
	return label;
};

const Guard = ({ children }: React.PropsWithChildren) => (
	<div className="mb-4 flex items-start gap-2 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-3 text-amber-900">
		<ShieldOutlinedIcon fontSize="small" className="mt-0.5 flex-none" />
		<Typography fontSize={13}>{children}</Typography>
	</div>
);

const Card = ({
	title,
	ai,
	children,
}: React.PropsWithChildren<{ title?: string; ai?: boolean }>) => (
	<section className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
		{title ? (
			<>
				<h2
					className={cn(
						"flex items-center gap-2 text-base font-bold",
						ai ? "text-fuchsia-700" : "text-zinc-900",
					)}
				>
					{ai ? <AutoAwesomeRoundedIcon fontSize="small" /> : null}
					{title}
				</h2>
				<div className="my-3 h-px bg-zinc-200" />
			</>
		) : null}
		{children}
	</section>
);

const directionArrow = (d: WoundTrajectoryDirection) =>
	d === "down" ? "↓ " : d === "up" ? "↑ " : "";

function RouteComponent() {
	const { t, i18n } = useTranslation("askhemi");
	const isZh = i18n.language === "zh-CN";
	const caseId = Route.useParams({ select: (s) => s.id });
	const shareId = Route.useSearch({ select: (s) => s.shareId });
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { createMedicalNote } = useCaseMutation();

	const [activeId, setActiveId] = useState<number | null>(null);
	const [stepIdx, setStepIdx] = useState(0);
	const [insertedNotes, setInsertedNotes] = useState(false);
	const [addedPlanIndices, setAddedPlanIndices] = useState<
		Record<number, boolean>
	>({});

	const addPlanDraft = useCaseClinicalStore((s) => s.addPlanDraft);

	const handleAddToPlan = (c: string, index: number) => {
		if (addedPlanIndices[index]) return;
		const extracted = extractPlan(c);
		addPlanDraft(caseId, {
			...extracted,
			route: "ai",
		});
		setAddedPlanIndices((prev) => ({ ...prev, [index]: true }));
		toast.success(t("analysis.addedToPlanToast"));
	};

	const shareQuery = useQuery({
		...getAskHemiShareQueryOptions(shareId as number),
		enabled: !!shareId,
	});
	const share = shareQuery.data;
	const photos = share?.photos ?? [];

	const listQuery = useQuery(listWoundAnalysesQueryOptions(caseId));
	const latestForShare = useMemo(() => {
		const items = (listQuery.data ?? []).filter(
			(a) =>
				a.status !== "failed" &&
				(a.askhemi_share_id == null || a.askhemi_share_id === shareId),
		);
		return [...items].sort(
			(a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf(),
		)[0] as WoundAnalysis | undefined;
	}, [listQuery.data, shareId]);

	const effectiveId = activeId ?? latestForShare?.id ?? null;
	const analysisQuery = useQuery(getWoundAnalysisQueryOptions(effectiveId));
	const analysis = analysisQuery.data;
	const inProgress =
		analysis?.status === "pending" || analysis?.status === "processing";

	const startMutation = useMutation<
		WoundAnalysis,
		ResError,
		{ caseId: number; shareId: number }
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CREATE_WOUND_ANALYSIS],
	});
	const sendMutation = useMutation<
		unknown,
		ResError,
		{ caseId: number; text: string }
	>({
		mutationKey: [QUERY_KEYS.MUTATE_SEND_CARE_CHAT_MESSAGE],
	});

	const showLoading =
		startMutation.isPending ||
		inProgress ||
		(!!effectiveId && !analysis && analysisQuery.isLoading);

	useEffect(() => {
		if (!showLoading) return;
		const iv = setInterval(
			() => setStepIdx((i) => Math.min(i + 1, STEP_KEYS.length - 1)),
			4000,
		);
		return () => clearInterval(iv);
	}, [showLoading]);

	const photoCount = photos.length || analysis?.photos_count || 0;
	const dayRange = useMemo(() => {
		if (photos.length > 1) {
			const first = photos[0].day_label.replace(/^Day\s*/i, "");
			const last = photos[photos.length - 1].day_label.replace(/^Day\s*/i, "");
			return isZh ? `第 ${first}–${last} 天` : `Day ${first}–${last}`;
		}
		if (photos.length === 1) {
			const num = photos[0].day_label.replace(/^Day\s*/i, "");
			return isZh ? `第 ${num} 天` : photos[0].day_label;
		}
		return analysis?.day_range != null ? String(analysis.day_range) : "";
	}, [photos, analysis?.day_range, isZh]);

	const onRun = () => {
		if (!shareId) return;
		setStepIdx(0);
		startMutation.mutate(
			{ caseId, shareId },
			{
				onSuccess: (res) => setActiveId(res.id),
				onError: (err) =>
					toast.error(err.response?.data?.message || t("analysis.runFailed")),
			},
		);
	};

	const runDate = analysis
		? formatDate(analysis.created_at) +
			(isZh ? " " : ", ") +
			fromMsToTime(dayjs(analysis.created_at).unix())
		: "";

	const buildNotes = (a: WoundAnalysis) => {
		const r = a.result;
		if (!r) return "";
		const lines: string[] = [t("analysis.notesHeading")];
		lines.push(
			t("analysis.notesSeries", {
				count: photoCount,
				range: dayRange,
			}),
		);
		lines.push("", `${t("analysis.trajectory")}:`, r.trajectory.summary);
		for (const m of r.trajectory.metrics) {
			const label = isZh
				? (METRIC_LABEL_TRANSLATIONS[m.label.toLowerCase().trim()] ?? m.label)
				: m.label;
			lines.push(`- ${label}: ${directionArrow(m.direction)}${m.value}`);
		}
		lines.push(
			`${t("analysis.confidence")}: ${t(
				`analysis.confidence${r.trajectory.confidence[0].toUpperCase()}${r.trajectory.confidence.slice(1)}`,
			)}`,
		);
		if (r.timepoints.length) {
			lines.push("", `${t("analysis.byTimepoint")}:`);
			for (const tp of r.timepoints)
				lines.push(`- ${formatDayLabel(tp.label, isZh)}: ${tp.observation}`);
		}
		if (r.flags.length) {
			lines.push("", `${t("analysis.attention")}:`);
			for (const f of r.flags) lines.push(`- ${f}`);
		}
		if (r.considerations.length) {
			lines.push("", `${t("analysis.consider")}:`);
			r.considerations.forEach((c, i) => lines.push(`${i + 1}. ${c}`));
		}
		if (r.sources.length) {
			lines.push("", `${t("analysis.retrievedFrom")}:`);
			r.sources.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
		}
		lines.push(
			"",
			t("analysis.notesDisclaimer", {
				name: a.requested_by_name ?? "",
				date: runDate,
			}),
		);
		return lines.join("\n");
	};

	const onInsertNotes = () => {
		if (!analysis || insertedNotes) return;
		createMedicalNote.mutate(
			{
				id: caseId,
				text: buildNotes(analysis),
				status: "draft",
				wound_analysis_id: analysis.id,
			},
			{
				onSuccess: () => {
					setInsertedNotes(true);
					toast.success(t("analysis.insertedIntoNotes"));
				},
				onError: () => toast.error(t("analysis.insertFailed")),
			},
		);
	};

	const goToCareChat = () =>
		navigate({
			to: "/dashboard/case/view/$id",
			params: { id: caseId },
			search: { tab: CASE_TABS_VALUE.CARECHAT },
		});

	const onSendToCareChat = async () => {
		const summary = analysis?.result?.patient_summary;
		if (!summary) return;
		try {
			const cc = await queryClient.fetchQuery({
				...getCareChatQueryOptions(caseId),
				staleTime: 0,
			});
			if (cc.care_chat?.status !== "open") {
				toast.error(t("analysis.startCareChatFirst"));
				return goToCareChat();
			}
			await sendMutation.mutateAsync({ caseId, text: summary });
			toast.success(t("analysis.summarySent"));
			return goToCareChat();
		} catch (err) {
			const e = err as ResError;
			toast.error(e.response?.data?.message || t("analysis.sendFailed"));
		}
	};

	const result = analysis?.status === "complete" ? analysis.result : null;
	const confidencePct = { low: 33, moderate: 66, high: 100 };

	return (
		<>
			<BackAppBar title={t("analysis.title")} className="pb-3">
				{share ? (
					<div className="flex flex-wrap items-center gap-2 text-sm">
						<span>
							{share.patient_name}
							{share.patient_age
								? ` · ${share.patient_age} ${isZh ? "岁" : "yo"}`
								: ""}
						</span>
						{photoCount ? (
							<span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-bold text-fuchsia-700">
								{t("analysis.photosRange", {
									count: photoCount,
									range: dayRange,
								})}
							</span>
						) : null}
					</div>
				) : null}
			</BackAppBar>
			<div className="container flex max-w-3xl flex-col gap-4 py-4">
				{!shareId ? (
					<Card>
						<Typography fontSize={14}>{t("analysis.noShareId")}</Typography>
					</Card>
				) : shareQuery.error?.response?.status === 410 ? (
					<Card>
						<Typography fontWeight={700} fontSize={14}>
							{t("accessRemovedTitle")}
						</Typography>
						<Typography fontSize={13}>{t("accessRemovedBody")}</Typography>
					</Card>
				) : null}

				{shareId && !showLoading && !analysis && !listQuery.isLoading ? (
					<Card title={t("analysis.whatThisDoes")} ai>
						<Typography fontSize={14} className="!mb-4 !text-zinc-700">
							{t("analysis.intro")}
						</Typography>
						<Guard>{t("analysis.guard")}</Guard>
						<button
							type="button"
							disabled={!photoCount || startMutation.isPending}
							onClick={onRun}
							className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
						>
							<AutoAwesomeRoundedIcon fontSize="small" />
							{t("analysis.run", { count: photoCount })}
						</button>
					</Card>
				) : null}

				{showLoading ? (
					<Card title={t("analysis.analysing")} ai>
						<div className="space-y-3">
							{["90%", "70%", "82%", "46%"].map((w) => (
								<div
									key={w}
									style={{ width: w }}
									className="h-3 animate-pulse rounded bg-zinc-200"
								/>
							))}
						</div>
						<Typography fontSize={13} className="!mt-3 !text-zinc-500">
							{t(`analysis.${STEP_KEYS[stepIdx]}`)}
						</Typography>
					</Card>
				) : null}

				{analysis?.status === "failed" ? (
					<Card title={t("analysis.failedTitle")}>
						<Typography fontSize={13} className="!mb-3 !text-red-600">
							{t("analysis.failedBody")}
						</Typography>
						<button
							type="button"
							onClick={onRun}
							disabled={startMutation.isPending}
							className="rounded-lg border border-violet-600 px-4 py-2 text-sm font-bold text-violet-700 hover:bg-violet-50"
						>
							{t("retry")}
						</button>
					</Card>
				) : null}

				{result && analysis ? (
					<>
						<Card title={t("analysis.trajectory")} ai>
							<Typography fontSize={14} className="!mb-3 !text-zinc-700">
								{result.trajectory.summary}
							</Typography>
							{result.trajectory.metrics.map((m) => (
								<div
									key={m.label}
									className="flex items-center justify-between border-b border-zinc-100 py-2 text-sm last:border-0"
								>
									<span className="text-zinc-600">
										{isZh
											? (METRIC_LABEL_TRANSLATIONS[
													m.label.toLowerCase().trim()
												] ?? m.label)
											: m.label}
									</span>
									<b
										className={m.direction === "down" ? "text-emerald-600" : ""}
									>
										{directionArrow(m.direction)}
										{m.value}
									</b>
								</div>
							))}
							<div className="mt-3 flex items-center gap-3 text-sm">
								<span className="text-zinc-600">
									{t("analysis.confidence")}
								</span>
								<span className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-200">
									<i
										className="block h-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600"
										style={{
											width: `${confidencePct[result.trajectory.confidence] ?? 0}%`,
										}}
									/>
								</span>
								<b>
									{t(
										`analysis.confidence${result.trajectory.confidence[0].toUpperCase()}${result.trajectory.confidence.slice(1)}`,
									)}
								</b>
							</div>
						</Card>

						{result.timepoints.length ? (
							<Card title={t("analysis.byTimepoint")}>
								<div className="space-y-3">
									{result.timepoints.map((tp) => (
										<div key={tp.label} className="flex gap-3">
											<span className="w-16 flex-none text-sm font-bold text-violet-700">
												{formatDayLabel(tp.label, isZh)}
											</span>
											<Typography fontSize={14}>{tp.observation}</Typography>
										</div>
									))}
								</div>
							</Card>
						) : null}

						{result.flags.length ? (
							<Card title={t("analysis.attention")}>
								<div className="space-y-2">
									{result.flags.map((f) => (
										<div
											key={f}
											className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-amber-900"
										>
											<ErrorOutlineRoundedIcon
												fontSize="small"
												className="mt-0.5 flex-none"
											/>
											<Typography fontSize={13}>{f}</Typography>
										</div>
									))}
								</div>
							</Card>
						) : null}

						{result.considerations.length ? (
							<Card title={t("analysis.consider")}>
								<Typography fontSize={13} className="!mb-3 !text-zinc-600">
									{t("analysis.considerLead")}
								</Typography>
								<div className="space-y-3">
									{result.considerations.map((c, i) => (
										<div
											key={c}
											className="flex flex-col gap-2 border-b border-zinc-100 pb-3 last:border-0 last:pb-0"
										>
											<div className="flex gap-3">
												<span className="w-6 flex-none text-sm font-bold text-violet-700">
													{i + 1}
												</span>
												<div className="flex-1">
													<Typography fontSize={14}>{c}</Typography>
													<button
														type="button"
														disabled={addedPlanIndices[i]}
														onClick={() => handleAddToPlan(c, i)}
														className="mt-2.5 inline-flex items-center gap-1 rounded-lg border border-sky-300 bg-white px-3 py-1 text-xs font-bold text-sky-700 hover:bg-sky-50 disabled:border-zinc-200 disabled:text-zinc-400 disabled:bg-zinc-50"
													>
														{addedPlanIndices[i]
															? t("analysis.addedAsDraft")
															: t("analysis.addToPlan")}
													</button>
												</div>
											</div>
										</div>
									))}
								</div>
							</Card>
						) : null}

						{result.sources.length ? (
							<Card title={t("analysis.retrievedFrom")}>
								<div className="space-y-2">
									{result.sources.map((s, i) => (
										<div key={s} className="flex gap-3 text-sm">
											<span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
												{i + 1}
											</span>
											<span>{s}</span>
										</div>
									))}
								</div>
								<Typography fontSize={12} className="!mt-3 !text-zinc-500">
									{t("analysis.retrievedDisclaimer")}
								</Typography>
							</Card>
						) : null}

						<Card>
							<Guard>
								{t("analysis.runBy", {
									name: analysis.requested_by_name ?? "",
									date: runDate,
								})}
							</Guard>
							<div className="flex flex-wrap gap-3">
								<button
									type="button"
									onClick={onInsertNotes}
									disabled={createMedicalNote.isPending || insertedNotes}
									className="flex-1 rounded-lg border border-violet-600 px-4 py-2.5 text-sm font-bold text-violet-700 hover:bg-violet-50 disabled:opacity-50"
								>
									{t("analysis.insertIntoNotes")}
								</button>
								<button
									type="button"
									onClick={onSendToCareChat}
									disabled={sendMutation.isPending || !result.patient_summary}
									className="flex-1 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-50"
								>
									{t("analysis.sendToCareChat")}
								</button>
							</div>
							<button
								type="button"
								onClick={onRun}
								disabled={startMutation.isPending}
								className="mt-3 w-full text-center text-sm font-semibold text-violet-700 underline"
							>
								{t("analysis.runAgain")}
							</button>
						</Card>
					</>
				) : null}
			</div>
		</>
	);
}
